import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ProjectsApi, ProjectShareSettings } from "@/services/api/projects";
import { useAuthSession } from "@/hooks/useAuthSession";
import { Project, User } from "@/types";
import { logger } from "@/utils";
import {
  Share2,
  Copy,
  Link,
  Link2,
  Users,
  Trash2,
  Plus,
  Globe,
  Lock,
  Check,
  AlertCircle,
  UserPlus,
} from "lucide-react";
import { ConnectionsShareTab } from "./ConnectionsShareTab";
import { FOREST_GREEN } from "@/styles/tokens";

interface ProjectSharingDialogProps {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
}

interface CollaboratorInvite {
  email: string;
  role: "owner" | "editor" | "viewer";
}

export const ProjectSharingDialog: React.FC<ProjectSharingDialogProps> = ({
  project,
  isOpen,
  onClose,
}) => {
  const { extendSession } = useAuthSession();

  // State
  const [shareSettings, setShareSettings] =
    useState<ProjectShareSettings | null>(null);
  const [collaborators, setCollaborators] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // New collaborator state
  const [newCollaborator, setNewCollaborator] = useState<CollaboratorInvite>({
    email: "",
    role: "viewer",
  });
  const [isInviting, setIsInviting] = useState(false);

  // Load sharing settings and collaborators
  useEffect(() => {
    if (isOpen) {
      loadSharingData();
    }
  }, [isOpen]);

  const loadSharingData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      extendSession();

      const [settingsResponse, collaboratorsResponse] = await Promise.all([
        ProjectsApi.getShareSettings(project.id),
        ProjectsApi.getProjectCollaborators(project.id),
      ]);

      if (settingsResponse.success && settingsResponse.data) {
        setShareSettings(settingsResponse.data);
      }

      if (collaboratorsResponse.success && collaboratorsResponse.data) {
        setCollaborators(collaboratorsResponse.data);
      }
    } catch (err) {
      logger.error("Error loading sharing data:", err);
      setError("Failed to load sharing settings");
    } finally {
      setIsLoading(false);
    }
  };

  const updateShareSettings = async (
    updates: Partial<ProjectShareSettings>,
  ) => {
    if (!shareSettings) return;

    try {
      setIsUpdating(true);
      setError(null);

      const response = await ProjectsApi.updateShareSettings(
        project.id,
        updates,
      );
      if (response.success && response.data) {
        setShareSettings(response.data);
        logger.info("Share settings updated", {
          projectId: project.id,
          updates,
        });
      } else {
        setError("Failed to update share settings");
      }
    } catch (err) {
      logger.error("Error updating share settings:", err);
      setError("Failed to update share settings");
    } finally {
      setIsUpdating(false);
    }
  };

  const generateShareLink = async () => {
    try {
      setIsUpdating(true);
      const response = await ProjectsApi.generateShareLink(project.id);

      if (response.success && response.data && shareSettings) {
        setShareSettings({
          ...shareSettings,
          shareLink: response.data.shareLink,
        });
      }
    } catch (err) {
      logger.error("Error generating share link:", err);
      setError("Failed to generate share link");
    } finally {
      setIsUpdating(false);
    }
  };

  const revokeShareLink = async () => {
    try {
      setIsUpdating(true);
      await ProjectsApi.revokeShareLink(project.id);

      if (shareSettings) {
        setShareSettings({
          ...shareSettings,
          shareLink: undefined,
        });
      }
    } catch (err) {
      logger.error("Error revoking share link:", err);
      setError("Failed to revoke share link");
    } finally {
      setIsUpdating(false);
    }
  };

  const copyShareLink = async () => {
    if (shareSettings?.shareLink) {
      try {
        await navigator.clipboard.writeText(shareSettings.shareLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        logger.error("Failed to copy share link:", err);
      }
    }
  };

  const inviteCollaborator = async () => {
    if (!newCollaborator.email.trim()) {
      setError("Email is required");
      return;
    }

    try {
      setIsInviting(true);
      setError(null);

      const response = await ProjectsApi.addCollaborator(project.id, {
        userId: newCollaborator.email, // Assuming email can be used as userId for invites
        role: newCollaborator.role,
      });

      if (response.success) {
        setNewCollaborator({ email: "", role: "viewer" });
        await loadSharingData(); // Reload to get updated collaborators
      }
    } catch (err) {
      logger.error("Error inviting collaborator:", err);
      setError("Failed to send invitation");
    } finally {
      setIsInviting(false);
    }
  };

  const removeCollaborator = async (userId: string) => {
    try {
      await ProjectsApi.removeCollaborator(project.id, userId);
      await loadSharingData(); // Reload to get updated collaborators
    } catch (err) {
      logger.error("Error removing collaborator:", err);
      setError("Failed to remove collaborator");
    }
  };

  const updateCollaboratorRole = async (userId: string, newRole: string) => {
    try {
      await ProjectsApi.updateCollaboratorRole(project.id, userId, newRole);
      await loadSharingData(); // Reload to get updated collaborators
    } catch (err) {
      logger.error("Error updating collaborator role:", err);
      setError("Failed to update collaborator role");
    }
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Loading...
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share "{project.name}"
          </DialogTitle>
          <DialogDescription>
            Manage who can access and collaborate on this project.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mx-6">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mx-6" style={{ maxWidth: 'calc(100% - 48px)' }}>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="collaborators">Collaborators</TabsTrigger>
            <TabsTrigger value="connections" className="flex items-center gap-1">
              <Link2 className="h-3 w-3" />
              Connections
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="mt-4">
            <div className="space-y-6 px-6">
              {/* Public Access */}
              <div className="space-y-4 pb-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold flex items-center gap-3 pb-1">
              {shareSettings?.isPublic ? (
                <Globe className="h-5 w-5 text-primary" />
              ) : (
                <Lock className="h-5 w-5 text-muted-foreground" />
              )}
              Public Access
            </h3>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="public-access" className="text-sm font-medium cursor-pointer">
                  Make this project public
                </Label>
                <p className="text-sm text-muted-foreground">
                  Public projects can be viewed by anyone on the internet.
                </p>
              </div>
              <Switch
                id="public-access"
                checked={shareSettings?.isPublic || false}
                onCheckedChange={(checked) =>
                  updateShareSettings({ isPublic: checked })
                }
                disabled={isUpdating}
              />
            </div>

            {shareSettings?.isPublic && (
              <div className="space-y-3 pl-4 border-l-2 border-gray-200">
                <div className="flex items-center justify-between py-2">
                  <Label htmlFor="allow-comments" className="text-sm cursor-pointer">
                    Allow comments
                  </Label>
                  <Switch
                    id="allow-comments"
                    checked={shareSettings?.allowComments || false}
                    onCheckedChange={(checked) =>
                      updateShareSettings({ allowComments: checked })
                    }
                    disabled={isUpdating}
                  />
                </div>

                <div className="flex items-center justify-between py-2">
                  <Label htmlFor="allow-forks" className="text-sm cursor-pointer">
                    Allow forks
                  </Label>
                  <Switch
                    id="allow-forks"
                    checked={shareSettings?.allowForks || false}
                    onCheckedChange={(checked) =>
                      updateShareSettings({ allowForks: checked })
                    }
                    disabled={isUpdating}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Share Link */}
          <div className="space-y-4 pb-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold flex items-center gap-3 pb-1">
              <Link className="h-5 w-5 text-muted-foreground" />
              Share Link
            </h3>

            {shareSettings?.shareLink ? (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Input
                    value={shareSettings.shareLink}
                    readOnly
                    className="flex-1 bg-gray-50"
                  />
                  <Button
                    variant="outline"
                    onClick={copyShareLink}
                    disabled={isUpdating}
                    className="flex items-center gap-2 min-w-[100px]"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                </div>
                <Button
                  variant="outline"
                  onClick={revokeShareLink}
                  disabled={isUpdating}
                  className="text-destructive hover:text-destructive hover:bg-red-50"
                >
                  Revoke Link
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <Button
                  onClick={generateShareLink}
                  disabled={isUpdating}
                  className="flex items-center gap-2"
                >
                  <Link className="h-4 w-4" />
                  Generate Share Link
                </Button>
                <p className="text-sm text-muted-foreground">
                  Anyone with the link can view this project.
                </p>
              </div>
            )}
          </div>
            </div>
          </TabsContent>

          <TabsContent value="collaborators" className="mt-4">
            <div className="space-y-6 px-6">
              {/* Collaborators */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-3 pb-1">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  Collaborators
                </h3>

                {/* Add new collaborator */}
                <div className="flex gap-3">
                  <Input
                    placeholder="Enter email address"
                    value={newCollaborator.email}
                    onChange={(e) =>
                      setNewCollaborator({
                        ...newCollaborator,
                        email: e.target.value,
                      })
                    }
                    className="flex-1"
                  />
                  <Select
                    value={newCollaborator.role}
                    onValueChange={(value: "owner" | "editor" | "viewer") =>
                      setNewCollaborator({
                        ...newCollaborator,
                        role: value,
                      })
                    }
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={inviteCollaborator}
                    disabled={isInviting || !newCollaborator.email.trim()}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Invite
                  </Button>
                </div>

                {/* Collaborators list */}
                <div className="space-y-3">
                  {collaborators.map((collaborator) => (
                    <div
                      key={collaborator.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                          {collaborator.firstName?.[0] || collaborator.username[0]}
                        </div>
                        <div>
                          <div className="font-medium text-sm">
                            {collaborator.firstName} {collaborator.lastName}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {collaborator.email}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Select
                          value={collaborator.role}
                          onValueChange={(value) =>
                            updateCollaboratorRole(collaborator.id, value)
                          }
                        >
                          <SelectTrigger className="w-[100px] h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                            <SelectItem value="owner">Owner</SelectItem>
                          </SelectContent>
                        </Select>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeCollaborator(collaborator.id)}
                          className="text-destructive hover:text-destructive hover:bg-red-50 h-9 w-9 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {collaborators.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50 rounded-lg">
                      <UserPlus className="h-10 w-10 text-muted-foreground mb-3" />
                      <p className="text-sm text-muted-foreground text-center">
                        No collaborators yet. Invite someone to start collaborating!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="connections" className="mt-4">
            <div className="px-6">
              <ConnectionsShareTab
                projectId={project.id}
                existingCollaboratorIds={collaborators.map((c) => c.id)}
                onCollaboratorAdded={loadSharingData}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="pt-6 mt-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectSharingDialog;
